/**
 * Bridges a normalized health-store session (`RecordedSessionSample`) into the
 * local-api create input for the synced `RecordedSession` table. Pure and
 * UI-free so it runs under the app's `test:unit` project — it owns the
 * Date→ISO string conversion and the "absent metric → null, never undefined"
 * rule the local row requires.
 *
 * The `id` is a **deterministic** `uuidv5(source|externalId)` (matching
 * `BodyMeasurementMapper`), so a re-import of the same session carries the same
 * primary key and the `ON CONFLICT(id)` upsert (client and server) is
 * idempotent — no composite unique, no `unique_violation` wedge (Vikunja #58).
 * It deliberately excludes the workout, so re-matching the same session to a
 * different workout updates the existing row rather than forking a duplicate.
 */
import { RandomUtils } from '@fytness/shared';
import type { RecordedSessionSample } from '../health-provider/health-provider.types';
import type { CreateRecordedSessionInput } from '@/lib/local-api/recorded-session';

export interface RecordedSessionLink {
  workoutId: string;
  /** Denormalized owner id, so the row syncs to its user via PowerSync. */
  userId: string;
}

export class RecordedSessionMapper {
  static toCreateInput(
    sample: RecordedSessionSample,
    link: RecordedSessionLink,
  ): CreateRecordedSessionInput {
    return {
      id: RandomUtils.deterministicUuid(
        `${sample.source}|${sample.externalId}`,
      ),
      source: sample.source,
      externalId: sample.externalId,
      startedAt: sample.startedAt.toISOString(),
      endedAt: sample.endedAt.toISOString(),
      avgHeartRate: sample.avgHeartRate ?? null,
      maxHeartRate: sample.maxHeartRate ?? null,
      activeCalories: sample.activeCalories ?? null,
      recordedDuration: sample.durationSec ?? null,
      workoutId: link.workoutId,
      userId: link.userId,
    };
  }
}
